// Combined Reducer
// Delegates to domain-specific reducers based on action type

import type { ClaimState, ClaimAction } from './types';
import { phaseReducer } from './phaseReducer';
import { segmentReducer } from './segmentReducer';
import { passengerReducer } from './passengerReducer';
import { documentReducer } from './documentReducer';
import { expenseReducer } from './expenseReducer';

// ============================================================================
// Initial State
// ============================================================================

export const initialState: ClaimState = {
  phase: 'welcome',
  previousPhase: null,

  caseId: null,
  caseNumber: null,
  isDirty: false,
  isAutoSaving: false,
  lastSavedAt: null,

  // Pending token (before case is created)
  pendingToken: null,
  tokenExpiresAt: null,
  tokenExpired: false,

  extractedData: null,
  editableSegments: [],
  passengers: [],
  pnr: null,
  bookingDate: null,

  incidentType: null,
  affectedSegmentId: null,
  incidentDescription: '',

  documents: [],
  expenses: [],

  narrative: '',

  error: null,

  // Email forwarding
  inboundEmailAddress: null,
  awaitingEmailSince: null,
};

// ============================================================================
// Combined Reducer
// ============================================================================

export function claimReducer(state: ClaimState, action: ClaimAction): ClaimState {
  // Try phase reducer
  const phaseResult = phaseReducer(state, action);
  if (phaseResult) return phaseResult;

  // Try segment reducer
  const segmentResult = segmentReducer(state, action);
  if (segmentResult) {
    // For SET_EXTRACTED_DATA, also need to update passengers
    if (action.type === 'SET_EXTRACTED_DATA') {
      const passengerResult = passengerReducer(segmentResult, action);
      return passengerResult || segmentResult;
    }
    return segmentResult;
  }

  // Try passenger reducer
  const passengerResult = passengerReducer(state, action);
  if (passengerResult) return passengerResult;

  // Try document reducer
  const documentResult = documentReducer(state, action);
  if (documentResult) return documentResult;

  // Try expense reducer
  const expenseResult = expenseReducer(state, action);
  if (expenseResult) return expenseResult;

  // Handle remaining actions
  switch (action.type) {
    // Incident data
    case 'SET_INCIDENT_TYPE':
      return {
        ...state,
        incidentType: action.incidentType,
        isDirty: true,
      };

    case 'SET_AFFECTED_SEGMENT':
      return {
        ...state,
        affectedSegmentId: action.segmentId,
        isDirty: true,
      };

    case 'SET_INCIDENT_DESCRIPTION':
      return {
        ...state,
        incidentDescription: action.description,
        isDirty: true,
      };

    // Narrative
    case 'SET_NARRATIVE':
      return {
        ...state,
        narrative: action.narrative,
        isDirty: true,
      };

    // Persistence
    case 'SET_CASE_ID':
      return {
        ...state,
        caseId: action.caseId,
        caseNumber: action.caseNumber,
      };

    case 'SET_DIRTY':
      return {
        ...state,
        isDirty: action.isDirty,
      };

    case 'SET_AUTO_SAVING':
      return {
        ...state,
        isAutoSaving: action.isAutoSaving,
      };

    case 'SET_LAST_SAVED':
      return {
        ...state,
        lastSavedAt: action.timestamp,
        isDirty: false,
      };

    // Email forwarding
    case 'SET_INBOUND_EMAIL_ADDRESS':
      return {
        ...state,
        inboundEmailAddress: action.emailAddress,
      };

    case 'SET_AWAITING_EMAIL_SINCE':
      return {
        ...state,
        awaitingEmailSince: action.timestamp,
      };

    // Pending token
    case 'SET_PENDING_TOKEN':
      return {
        ...state,
        pendingToken: action.token,
        inboundEmailAddress: action.emailAddress,
        tokenExpiresAt: action.expiresAt,
        tokenExpired: false,
      };

    case 'SET_TOKEN_EXPIRED':
      return {
        ...state,
        tokenExpired: action.expired,
      };

    case 'CLEAR_PENDING_TOKEN':
      return {
        ...state,
        pendingToken: null,
        tokenExpiresAt: null,
        tokenExpired: false,
      };

    // Reset
    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// Re-export types for convenience
export type { ClaimState, ClaimAction } from './types';
export { generateId } from './types';
